import type { DigitalVisitReason } from "@/lib/constants/digitalVisitReasons";
import {
  DISPATCH_REASONS,
  SCHEDULER_VARIANTS,
  type DispatchReason,
  type SchedulerVariant,
} from "./variants";

/**
 * Per-service configuration for the 3-step accordion scheduler pages and the
 * "You're scheduled" confirmation page (Clare Simizon's approved boards,
 * Figma file vy1bHbX6sIjly3EzwAQA62, canvas nodes 982:20412..982:20416).
 *
 * Every copy string is byte-verbatim from the Figma REST extract
 * (~/tag-docs/clare-scheduling-20260831/specs/*-raw.json) and cited by node
 * id, including em dashes, curly apostrophes and non-breaking spaces, with two
 * deliberate exceptions: the design's merge tokens ("[Denture repair]",
 * "{Name}", "## of", "##.# mi") are written as {token} placeholders that
 * ./content.ts fills at render time, and the superscript footnote digits the
 * design concatenates into text runs are lifted out into separate marker
 * fields (insuranceFootnoteMarker, savingsPlanFootnoteMarker, faqFootnoteMarker)
 * with a {footnote} placeholder marking where the <sup> renders.
 *
 * This file is the ONE place the scheduler's copy lives in code. ./content.ts
 * turns these rows into typed defaults, and every string is also a Studio
 * variable on the four presets (app/components/studio/ThreeStepScheduler/
 * presets.tsx) so TAG can edit it in Contentful without a release
 * (tag-delivery section 0). Strings the boards keep as HIDDEN layers stay here
 * for reference but ship as EMPTY defaults - see SHARED_COPY below.
 *
 * Booking configuration is NEVER invented here: reason and
 * questionnaireVisitReason come from the proven registries in ./variants.ts,
 * referenced by registrySlug so the two files cannot drift silently
 * (services.test.ts cross-checks every row). Checkup books as Other + OTHER,
 * TAG's own "Other/Check Up" radio (D15, Chris 2026-09-08; citations on the
 * checkup row in ./variants.ts) - no service is held.
 */

export type ThreeStepServiceKey =
  | "dentures"
  | "emergency"
  | "implants"
  | "checkup"
  | "clearaligners";

interface ServiceFaqItem {
  /** Verbatim question copy from the board. */
  question: string;
  /** Verbatim answer copy. Only fully authored answers render; the boards'
   * lorem-ipsum answer placeholders ship as "" so the question is seeded as a
   * Studio variable and the item stays hidden until TAG authors the answer
   * (G22). A {footnote} placeholder marks where the service's
   * faqFootnoteMarker renders as a <sup>. */
  answer: string;
  /** Verbatim footnote attached beneath the answer, when the board has one;
   * its leading superscript digit is the {footnote} placeholder. */
  footnote?: string;
}

export interface ServiceConfirmationContent {
  /** Appointment-card title, e.g. "Denture Appointment:" (1024:21409). */
  appointmentCardTitle: string;
  /** Journey/video section heading, e.g. "Your Denture Journey" (1024:21555). */
  journeyHeading: string;
  /** Video card title (verbatim). */
  journeyVideoTitle: string;
  /** Video card body (verbatim, incl. nbsp and em dashes where authored). */
  journeyVideoBody: string;
  /** "Have Questions?" subtitle (verbatim). */
  faqSubtitle: string;
  /**
   * The superscript digit the board attaches to this service's FAQ footnote
   * (and, on dentures, to the answer's "membership" run): "1" on emergency,
   * implants and motto (1024:24466, 1024:26531, 1024:30819), "3" on dentures
   * (1024:21613), none on checkup. Rendered as <sup> wherever {footnote}
   * appears in faq[].answer / faq[].footnote.
   */
  faqFootnoteMarker: string;
  faq: readonly ServiceFaqItem[];
  /**
   * Per-service photography, Brandfolder CDN URLs only. The boards bind a
   * different hero, FAQ header photo and video poster per service (variants
   * spec: emergency hero eebad5d8, FAQ ad6bb691, video ece298d0; dentures
   * 82427912 / 097c8e94 / 27f2f3fc, and so on). Unset falls back to the
   * shared office photographs in SHARED_COPY.confirmation; the video poster
   * and file have no shared fallback, so an unset pair hides the journey
   * section (G21) until TAG supplies the asset.
   */
  heroImageSrc?: string;
  heroImageAlt?: string;
  journeyPosterSrc?: string;
  journeyVideoSrc?: string;
  faqImageSrc?: string;
  faqImageAlt?: string;
}

export interface ThreeStepService {
  key: ThreeStepServiceKey;
  /**
   * The variants.ts registry row this service's booking config is sourced
   * from. reason/questionnaireVisitReason below MUST equal that row's values
   * (enforced by services.test.ts). Slugs: dentures, emergency, implants,
   * clear-aligners, checkup.
   */
  registrySlug: string;
  /** Page H1, byte-verbatim incl. the authored U+2028 line separator. */
  headline: string;
  /**
   * Resolves the boards' literal "[Denture repair]" merge token in the
   * subtitle "... visits are available at all of our 1,100 locations
   * nationwide." (e.g. 1024:21901) and the review heading
   * "[Denture repair] appointment review" (e.g. 1024:23364). The emergency
   * spec (§9.3) names this exact resolution: "Emergency visits are
   * available ...".
   */
  serviceName: string;
  /** DigitalVisitReason from the registry row (a verbatim prod radio). */
  reason: DigitalVisitReason;
  /** QuestionnaireBy class token from the registry row. */
  questionnaireVisitReason: string;
  /** Visit-type label from the registry row (eyebrow / dispatcher grammar). */
  visitTypeLabel: string;
  /**
   * Step 2 "Let us get to know you." service question (Yes/No). Only the
   * DENTURES board authors "Do you wear or have a denture?" on a visible
   * layer (M 1024:21231, D 1024:22518; review echo 1024:21384 / 1024:22676).
   * The same node exists on the emergency (1024:24083), implants
   * (1024:26148), checkup (1024:28213) and motto (1024:30436) desktop frames,
   * but on every one of them it sits under a visible=false ancestor
   * (1024:24080, 1024:26145, 1024:28210, 1024:30433) and so do the review
   * echoes (1024:24239, 1024:26304, 1024:28369, 1024:30592) - a hidden layer,
   * not authored content. The 2026-09-07 variants audit re-read every chain
   * (G-02 / D-07); figma-visibility-fixture.json pins it and services.test.ts
   * fails if a row ships the question its board hides.
   *
   * DATA DISCLOSURE - TODO(evans/joseph): the captured answer is echoed on
   * the Review step (dentures 1024:21384) but is NEVER transmitted on the
   * booking write: the frozen submitAppointment contract carries no field for
   * it. Decide where this answer should land (questionnaireAnsweredQuestions
   * on the slots read, or a booking field).
   */
  serviceQuestion?: string;
  confirmation: ServiceConfirmationContent;
}

/**
 * The Savings Plan footnote under FAQ item 1. Visible on the dentures board
 * only (1024:21613, marker 3); the identical node is a HIDDEN layer on the
 * emergency, implants, motto and checkup boards (1024:24466, 1024:26531,
 * 1024:30819, 1024:28596 - visible=false re-read over REST 2026-09-08, G23),
 * so those services ship no footnote. The leading superscript digit is the
 * {footnote} placeholder; the digit itself is the service's faqFootnoteMarker.
 */
const SAVINGS_PLAN_FAQ_FOOTNOTE =
  "{footnote}The Aspen Dental Savings Plan is NOT insurance, and costs $49/year. Offer available in select offices. Price is for a Basic single arch denture. Additional charges for relines and other required dentistry services, including extractions. Please see plan terms and conditions for details.";

/** "Do you wear or have a denture?" - dentures 1024:21231 (the only visible authoring). */
const DENTURE_QUESTION = "Do you wear or have a denture?";

export const THREE_STEP_SERVICES: readonly ThreeStepService[] = [
  {
    key: "dentures",
    registrySlug: "dentures",
    // 1024:21900 (desktop; the mobile twin 1024:20630 carries a stray LRE mark)
    headline: "Your Dentures Appointment is 3 Steps Away",
    serviceName: "Dentures",
    reason: "Dentures",
    questionnaireVisitReason: "DENTURES",
    visitTypeLabel: "Dentures",
    serviceQuestion: DENTURE_QUESTION, // 1024:21231
    confirmation: {
      appointmentCardTitle: "Denture Appointment:", // 1024:21409
      journeyHeading: "Your Denture Journey", // 1024:21555
      journeyVideoTitle: "What to Expect During the Dentures Process", // 1024:21560
      journeyVideoBody:
        "Starting your denture journey is a big step, and we want you to feel prepared and confident throughout the process. In this video, Dr. McDonough explains what to expect during the denture process and how Aspen Dental teams support patients every step of the way.", // 1024:21561
      faqSubtitle: "Your questions about denture appointments, answered.", // 1024:21602
      faqFootnoteMarker: "3", // 1024:21612 / 1024:21613 superscript run
      faq: [
        {
          question: "How much do dentures cost at Aspen Dental?", // 1024:21609
          answer:
            "It varies based on the type, materials and any care you may need beforehand, like extractions or implants. At Aspen Dental, we offer dentures starting at $499 per arch with the purchase of an Aspen Dental Savings Plan membership{footnote}, with comprehensive pricing and flexible financing options to help you plan with confidence.", // 1024:21612
          footnote: SAVINGS_PLAN_FAQ_FOOTNOTE, // 1024:21613 (same sentence, marker 3)
        },
        // Q2-Q4 (1024:21616, 1024:21622, 1024:21628) carry hidden lorem-ipsum
        // answers on the board: the questions are seeded so TAG can author the
        // answers in Contentful; an item renders only once its answer is set.
        { question: "Is same-day denture repair available?", answer: "" }, // 1024:21616
        { question: "Can my denture replacement be completed the same day?", answer: "" }, // 1024:21622
        { question: "Does insurance cover dentures?", answer: "" }, // 1024:21628
      ],
    },
  },
  {
    key: "emergency",
    registrySlug: "emergency",
    headline: "Schedule Today or Walk in", // Nathan Schwederske mock 2026-09-03 (was "Your Emergency Appointment is 3 Steps Away", 1024:22711)
    serviceName: "Emergency",
    reason: "Emergency",
    questionnaireVisitReason: "EMERGENCY",
    visitTypeLabel: "Emergency",
    // No service question: the desktop node 1024:24083 sits under the hidden
    // frame 1024:24080 and the review echo 1024:24241 under hidden
    // 1024:24239 (G-02 / D-07); the mobile frame 1024:23148 never had it.
    confirmation: {
      appointmentCardTitle: "Emergency Appointment:", // 1024:24262
      journeyHeading: "Your Emergency Appointment", // 1024:24408
      journeyVideoTitle: "What to Expect During an Emergency Appointment", // 1024:24413
      journeyVideoBody:
        "Dealing with a dental emergency can be stressful, and we want you to feel supported the moment you walk in. This video explains what to expect during your first visit — from how our team efficiently assesses your pain or injury to the options they'll walk you through for immediate relief and next steps in your care.", // 1024:24414
      faqSubtitle: "Your questions about emergency appointments, answered.", // 1024:24455
      // 1024:24466 (the Savings Plan footnote) is a hidden layer on this board, so
      // no FAQ item carries a footnote and the marker has nothing to mark (G23).
      faqFootnoteMarker: "",
      faq: [
        {
          question: "Is a broken, chipped or cracked tooth considered a dental emergency?", // 1024:24462
          answer:
            "If you have a broken, chipped or cracked tooth, it should be addressed immediately. The rough edges of your tooth can cause further damage to your mouth, and if left untreated, the damage to your tooth can worsen. As a first aid measure, you can gently rinse your mouth and cover any sharp edges with dental wax until you can come in for a visit. Make sure to avoid hard foods until you visit your emergency walk-in Aspen Dental dentist.", // 1024:24465
        },
        // Q2-Q4 (1024:24469, 1024:24475, 1024:24481) have hidden lorem answers on
        // the board; seeded as questions, rendered once TAG authors the answers.
        { question: "Should I treat a toothache as a dental emergency?", answer: "" }, // 1024:24469
        { question: "How much does emergency dental care cost?", answer: "" }, // 1024:24475
        {
          question: "What can expect when you walk in for a dental emergency at Aspen Dental?", // 1024:24481 (verbatim, board grammar)
          answer: "",
        },
      ],
    },
  },
  {
    key: "implants",
    registrySlug: "implants",
    headline: "Your Implants Appointment is  3 Steps Away", // 1024:24776
    serviceName: "Implants",
    reason: "Implants",
    questionnaireVisitReason: "IMPLANTS",
    visitTypeLabel: "Implant Consult",
    // No service question: 1024:26148 sits under hidden 1024:26145, the
    // review echo 1024:26306 under hidden 1024:26304 (G-02 / D-07).
    confirmation: {
      appointmentCardTitle: "Implants Appointment:", // 1024:26327
      journeyHeading: "Your Implants Journey", // 1024:26473
      journeyVideoTitle: "What to Expect During the Implants Process", // 1024:26478
      journeyVideoBody:
        "Take the first step toward restoring your smile with confidence. In this video, Dr. McDonough explains what you can expect during your first dental implant visit, including oral health evaluations, digital X-rays or imaging, implant treatment options, and the next steps in your care plan.", // 1024:26479
      faqSubtitle: "Your questions about implants appointments, answered.", // 1024:26520
      faqFootnoteMarker: "", // 1024:26531 is a hidden layer (G23)
      faq: [
        {
          question: "How much do dental implants cost at Aspen Dental?", // 1024:26527
          answer:
            "Implant costs vary based location and is listed per arch or per implant: Fixed full arch price ranges from $17,270 to $27,666, Implant denture (with two implants) price ranges from $6,102 to $10,638, Single tooth implant price ranges from $2,859 to $4,597, Implant bridge price ranges from $5,884 to $9,920. Additional charges may apply for other required dentistry services, including extractions. The average cost displayed reflects the national average out-of-pocket cost per arch for a new implant treatment when using an Aspen Dental Savings Plan membership.", // 1024:26530 ("based location" is verbatim in the source)
        },
        { question: "What are the 4 types of dental implants?", answer: "" }, // 1024:26534
        { question: "How long does it take to get dental implants?", answer: "" }, // 1024:26540
        { question: "Can smokers receive dental implants?", answer: "" }, // 1024:26546
      ],
    },
  },
  {
    key: "checkup",
    registrySlug: "checkup",
    headline: "Your Checkup Appointment is  3 Steps Away", // 1024:26841
    serviceName: "Checkup",
    // D15 (Chris 2026-09-08): books with TAG's own checkup radio - Other plus
    // questionnaire class OTHER. The registry row `checkup` in ./variants.ts
    // carries the backend citations (aspen-scheduler swagger.json:3936-3945,
    // ReasonForVisit.tsx:72-77 "Other/Check Up" / filterBy OTHER).
    reason: "Other",
    questionnaireVisitReason: "OTHER",
    visitTypeLabel: "Checkup",
    // No service question: 1024:28213 and its review echo 1024:28371 are
    // hidden layers (ancestors 1024:28210 / 1024:28369 visible=false).
    confirmation: {
      appointmentCardTitle: "Checkup Appointment:", // 1024:28392
      journeyHeading: "Your Checkup Appointment", // 1024:28538
      journeyVideoTitle: "What to Expect at Your Checkup Appointment", // 1024:28791
      journeyVideoBody:
        "In this video, Dr. McDonough explains how Aspen Dental teams evaluate your oral health, discuss your goals, and help determine the next steps in your care. ", // 1024:28792
      faqSubtitle: "Your questions about checkup appointments, answered.", // 1024:28585
      faqFootnoteMarker: "", // the checkup FAQ footnote 1024:28596 is a hidden layer
      faq: [
        {
          question: "Is a dental checkup the same thing as a dental cleaning?", // 1024:28840
          answer:
            "No. At Aspen Dental a dental checkup may include services like polishing, but a full preventive cleaning is often a separate visit with a hygienist. Your care team can discuss next steps based on your oral health and book a follow-up preventive teeth cleaning.", // 1024:28843
        },
        {
          question: "What’s the difference between a dental checkup and a dental exam?",
          answer: "",
        }, // 1024:28599
        { question: "Can dental anxiety affect my checkup?", answer: "" }, // 1024:28605
        { question: "Are dental checkups covered by insurance?", answer: "" }, // 1024:28611
      ],
    },
  },
  {
    key: "clearaligners",
    registrySlug: "clear-aligners",
    headline: "Your Motto® Appointment is  3 Steps Away", // 1024:28906
    serviceName: "Motto®",
    reason: "ClearAligners",
    questionnaireVisitReason: "CLEAR_ALIGNERS",
    visitTypeLabel: "Clear Aligners",
    // No service question: 1024:30436 sits under hidden 1024:30433, the
    // review echo 1024:30594 under hidden 1024:30592 (the motto spec §8.3
    // already flagged it as template carryover).
    confirmation: {
      appointmentCardTitle: "Motto® Appointment:", // 1024:30615
      journeyHeading: "Your Motto Journey", // 1024:30761
      journeyVideoTitle: "What to Expect During the Motto Journey", // 1024:30766
      journeyVideoBody:
        "Starting your Motto journey is an exciting step, and we want you to feel confident and prepared throughout the process. In this video, you’ll learn about what to expect after booking your clear aligner appointment — from your first scan with an Aspen Dental provider to your final retainer — and how our teams support you every step of the way.", // 1024:30767
      faqSubtitle: "Your questions about Motto appointments, answered.", // 1024:30808
      faqFootnoteMarker: "", // 1024:30819 is a hidden layer (G23)
      faq: [
        {
          question: "How are Motto clear aligners different from other clear aligners?", // 1024:30815
          answer:
            "Motto combines high-quality aligners with expert, personalized care—often at a more affordable price than traditional options. Treatment is designed, monitored, and supported by dental professionals to help deliver consistent, reliable results. ", // 1024:30818
        },
        { question: "How much do Motto clear aligners cost?", answer: "" }, // 1024:30822
        { question: "What’s the difference between clear aligners and braces?", answer: "" }, // 1024:30828
        { question: "How long does treatment with Motto clear aligners take?", answer: "" }, // 1024:30834
      ],
    },
  },
] as const;

/**
 * DigitalVisitReason -> service page. BrokenTooth_ToothPain rides the
 * emergency service page (same EMERGENCY questionnaire class per
 * DISPATCH_REASONS). Other is deliberately NOT mapped: on its own it is the
 * dispatcher's generic "Something else" new visit (shared template, registry
 * heading via headlineForLabel). The checkup board also books as Other (D15)
 * but is reached only through its LP slug / preset (SERVICE_KEY_FOR_SLUG), so
 * a "Something else" pick never renders checkup copy.
 */
const SERVICE_KEY_FOR_REASON: Partial<Record<DigitalVisitReason, ThreeStepServiceKey>> = {
  Dentures: "dentures",
  Emergency: "emergency",
  BrokenTooth_ToothPain: "emergency",
  Implants: "implants",
  ClearAligners: "clearaligners",
};

/** Registry slug -> service page (LP deep links, ?variant=). */
const SERVICE_KEY_FOR_SLUG: Record<string, ThreeStepServiceKey> = {
  dentures: "dentures",
  "denture-repair": "dentures",
  implants: "implants",
  "tooth-replacement": "implants",
  "clear-aligners": "clearaligners",
  emergency: "emergency",
  "tooth-pain": "emergency",
  checkup: "checkup",
};

export function getThreeStepService(key: ThreeStepServiceKey): ThreeStepService {
  return THREE_STEP_SERVICES.find((s) => s.key === key)!;
}

export function serviceForReason(
  reason: DigitalVisitReason | null | undefined,
): ThreeStepService | null {
  if (!reason) return null;
  const key = SERVICE_KEY_FOR_REASON[reason];
  return key ? getThreeStepService(key) : null;
}

export function serviceForVariantSlug(slug: string | null | undefined): ThreeStepService | null {
  if (!slug) return null;
  const key = SERVICE_KEY_FOR_SLUG[slug];
  return key ? getThreeStepService(key) : null;
}

/**
 * H1 for reasons without a dedicated board (Other / "Something else"): the
 * boards' shared H1 grammar "Your [Service] Appointment is 3 Steps Away"
 * with the registry visitTypeLabel in the service slot, keeping the boards'
 * U+2028 separator before "3 Steps Away" (rendered as a line break, see
 * content.ts headlineLines). Never used when a dedicated service page exists.
 */
export function headlineForLabel(visitTypeLabel: string): string {
  return `Your ${visitTypeLabel} Appointment is  3 Steps Away`;
}

/** Registry row backing a service, for provenance display and tests. */
export function registryRowFor(service: ThreeStepService): SchedulerVariant | null {
  return SCHEDULER_VARIANTS.find((v) => v.slug === service.registrySlug) ?? null;
}

/** DISPATCH_REASONS row for a reason (shared registry accessor). */
export function dispatchRowFor(reason: DigitalVisitReason): DispatchReason | null {
  return DISPATCH_REASONS.find((r) => r.reason === reason) ?? null;
}

/**
 * Shared template copy (identical across all five boards), byte-verbatim.
 * Node ids cited from the dentures board unless noted; the sibling boards
 * carry the identical strings (see the per-service spec §1 tables).
 *
 * Hidden layers. Several strings here sit on layers with visible=false on
 * every board, dentures included: availabilityLine (1024:21901),
 * needAssistancePrefix (1024:21902), step1.heading / subheading in the
 * Nearest state (1024:20644..20647), the two step-2 intro pairs (heading /
 * subheading under 1024:21101, knowHeading / knowSubheading under
 * 1024:21189 - REST read 2026-09-08, hidden on all ten patient frames),
 * step2.dobHelper (1024:21148), the SMS consent row (1024:21167) and
 * step3.subheading (the review intro, 1024:22587). They are kept here for
 * reference and for the Studio variable
 * descriptions, but content.ts ships them as EMPTY defaults so they do not
 * render (decision D-01, 2026-09-07); an author can bring one back by typing
 * it into the placement's variable.
 *
 * Templates use {token} placeholders filled by content.ts fillTemplate:
 * {service}, {count}, {zip}, {miles}, {firstName}, {cityState}, {phone}
 * (rendered as the Invoca tel link), {callOffice}, {retry} and {footnote}
 * (rendered as a <sup> carrying the matching marker).
 */
export const SHARED_COPY = {
  /**
   * Contact phone for every "call us" site (assist line, help line, errors,
   * failure banners, confirmation). Same default as the frozen
   * SchedulingWidget's contactPhoneDisplay / contactPhoneNumber; Invoca
   * retargets the rendered anchor at runtime (useInvocaNumber).
   */
  contactPhoneDisplay: "(800) 277-3633",
  contactPhoneNumber: "+18002773633",
  /** "[Denture repair] visits are available ..." - HIDDEN layer 1024:21901. */
  availabilityLine: "{service} visits are available at all of our 1,100 locations nationwide.",
  needAssistancePrefix: "Need assistance? Call ", // HIDDEN layer 1024:21902 ("(###) ###-####" is the phone slot)
  needHelpPrefix: "Need help booking? Call ", // 1024:20777
  retry: "Retry",
  /** The "Required" tag beside every field label row (1024:22738 M / 1024:23491 D). */
  requiredTag: "Required",
  steps: {
    locationTime: "Location & Time", // 1024:20641
    patientInformation: "Patient Information", // 1024:20768
    review: "Review", // 1024:20775
  },
  step1: {
    heading: "Select a location and time that works for you.", // 1024:20646 - HIDDEN in the Nearest state (1024:20644)
    subheading: "Select an Aspen Dental location, then choose a date and time.", // 1024:20647 - HIDDEN with its heading
    zipLabel: "Search For An Office By ZIP Code", // 1024:20831
    zipPlaceholder: "ZIP Code", // 1024:20836
    /** The "Search" button is a HIDDEN layer on the emergency boards
     * (1024:22919 / 23496); this is the accessible name of the in-field
     * locate glyph that submits the ZIP form instead (G10, D-J). */
    search: "Search", // 1024:20840
    selectedLocation: "Selected Location", // 1024:20655 / 1024:22736 (D board 1024:23625 says "Select a Location", D-M)
    nearest: "Nearest", // 1024:20661 (rendered uppercase by CSS, G36)
    changeLocation: "Change Location", // 1024:20662
    /** Results heading. The board node is a placeholder, "## of Offices Near
     * [ZIP CODE]" (1024:20845 / 23067 / 23519, refuted R1); rendering its
     * "of" literally read "6 of Offices Near 13214" (G43). Plural and
     * singular are separate lines so both can be authored. */
    officesNear: "{count} Offices Near {zip}",
    officesNearOne: "{count} Office Near {zip}",
    showMore: "Show More", // 1024:21044 / 23125 (secondary pill, G30)
    chooseDate: "Choose a Date", // 1024:20675
    /** 9th/overflow day chip label (calendar-icon variant, fill #E7F0FC) -
     * 1024:20705; its "Mon. ##th" date placeholder is 1024:20706. */
    chooseDateChip: "Choose Date", // 1024:20705
    /** Date line of a day chip, under the weekday. The desktop board's "Mon.
     * ##th" (1024:23648 et al, D-11): AP month abbreviation, day, ordinal.
     * The mobile board's "Sept. 27" (1024:22762) is "{month} {day}". */
    dayChipDate: "{month} {day}{ordinal}",
    /** One line over the day strip when the first day with times is not
     * today (prod audit 2026-09-11, #27: production office 1103's emergency
     * availability began six days out under a "Schedule Today" h1, and the
     * strip had slid past Today with nothing saying why). {date} is the
     * pressed chip's own two lines on one line, "Wednesday Sept. 16th". */
    nextAvailable: "Next available: {date}",
    /** Accessible names of the strip's Earlier / Later arrows (from 960, for
     * pointer users, who have no horizontal scroll of their own; a swipe is
     * the rail below that). Never visible text. */
    earlierDays: "Earlier days",
    laterDays: "Later days",
    chooseTime: "Choose a Time", // 1024:20710
    morning: "Morning", // 1024:20715
    afternoon: "Afternoon", // 1024:20738
    showAll: "Show All", // 1024:20736 / 22817 (desktop board 1024:23711 says "Load More" - D-K keeps Show All)
    milesAway: "{miles} mi away", // 1024:20669 "##.# mi away"
    continueCta: "Continue To: Patient Information", // 1024:20762
    // ZIP search and availability states. The boards carry no error or
    // loading strings; these are the scheduler's own, verbatim from the
    // first release, now authorable.
    zipInvalidError: "Please enter a valid 5-digit ZIP code.",
    officeNotFoundError: "We could not find that office. Search a ZIP code above or call {phone}.",
    officeSearchUnavailable:
      "Office search is unavailable right now. You can also call {phone} for help finding an office.",
    noOfficesTemplate: "No offices found near {zip}. Try another ZIP code.",
    loadingTimes: "Loading available times...",
    timesLoadFailed: "We could not load times. {retry} or {callOffice}.",
    callOffice: "call the office",
    noTimesThisDay: "No times left this day. Try another day or {callOffice}.",
    slotTakenError:
      "We couldn't book that time. It may have just been taken. Pick another time, or call {phone}.",
  },
  step2: {
    /** Intro pair over the fields - HIDDEN layer on every board: the "Section
     * Text" frame carries visible=false at M (dentures 1024:21101; emergency
     * 23182, implants 25247, checkup 27312, motto 29377) and at D (dentures
     * 1024:22417; 23982, 26047, 28112, 30335). Shipped empty (D-01). */
    heading: "Tell us who the visit is for.", // 1024:21103
    subheading:
      "Everything the office needs to hold the appointment time. No account needed and no payment now.", // 1024:21104
    /** "Let us get to know you." pair over the Yes/No groups - HIDDEN layer on
     * every board the same way (M 1024:21189; 23270, 25335, 27400, 29465 - D
     * 1024:22496; 24061, 26126, 28191, 30414). Shipped empty (D-01). */
    knowHeading: "Let us get to know you.", // 1024:21191
    knowSubheading:
      "Provide more information on your current dental health to help us schedule your appointment.", // 1024:21192
    firstName: "First Name", // 1024:21112
    lastName: "Last Name", // 1024:21125
    dateOfBirth: "Date of Birth", // 1024:21137
    dobPlaceholder: "mm/dd/yyyy", // 1024:21142
    dobHelper: "We need your date of birth to ensure appropriate treatment.", // 1024:21148 - HIDDEN layer
    phone: "Mobile Phone Number", // 1024:21153
    phonePlaceholder: "XXX-XXX-XXXX", // 1024:21158
    phoneHelper: "If we need to call you to change your appointment. ", // 1024:21163 (trailing space verbatim)
    smsConsent:
      "Check to receive text messages about your appointment from Aspen Dental. Message and data rates may apply. Reply STOP to opt out.", // 1024:21167 - HIDDEN layer; row gated by showSmsConsent (D-05)
    email: "Email Address", // 1024:21176
    emailPlaceholder: "email@domain.com", // 1024:21181
    insuranceQuestion: "Plan on using dental insurance?", // 1024:21219 (canonical both-breakpoint phrasing)
    medicaidNote: "We do not accept Medicaid", // 1024:21227
    yes: "Yes", // 1024:21224
    no: "No", // 1024:21226
    required: "Required", // 1024:21221
    continueCta: "Continue To: Review", // 1024:21214
  },
  /**
   * Guarantor (parent/guardian) sub-section copy - TAG-171. Shown on the Patient
   * Information step only when the entered DOB is a minor AND the placement's
   * showGuardianFields is on (default on: the guardian's name and phone ride on
   * the booking write as the enterprise `guardian`; the relationship has no
   * field there and is echoed on Review only). No Figma node: this is a new
   * flow the boards did not cover; copy is provisional and flagged to TAG for
   * confirmation (heading, subheading, and the relationship option set).
   */
  guarantor: {
    heading: "Parent or guardian information",
    subheading:
      "This patient is under 18. Please provide a parent or guardian who can authorize the visit.",
    firstName: "Parent or guardian first name",
    lastName: "Parent or guardian last name",
    relationship: "Relationship to patient",
    relationshipPlaceholder: "Select one",
    phone: "Parent or guardian mobile phone number",
  },
  step3: {
    /** "[Denture repair] appointment review" with the token resolved. */
    heading: "{service} appointment review", // 1024:21294
    /**
     * Review intro - a HIDDEN layer on every board (dentures 1024:22587,
     * emergency 1024:24152, implants 1024:26217, checkup 1024:28282, motto
     * 1024:30505). The lines kept here are the client-edited version (Joseph
     * Solita, 2026-09: "Request" dropped, the exact-time line removed);
     * shipped as an empty default per D-01.
     */
    subheading: "Select ‘Confirm My Appointment’ to complete your scheduling.\nNo payment due now",
    officeLocation: "Office Location", // 1024:21305
    dateTime: "Date & Time", // 1024:21321
    patientInformation: "Patient Information", // 1024:21344
    date: "Date", // 1024:23401 (emergency board)
    time: "Time", // 1024:23406
    /** The "Date:" value, "DayOfWeek Mon. ##rd" on the board (1024:23403 /
     * 24190): the full weekday, then the day chips' AP month and ordinal day.
     * Tokens {weekday}, {month}, {day}, {ordinal}; renders "Friday Sept. 11th". */
    dateTemplate: "{weekday} {month} {day}{ordinal}",
    edit: "Edit", // 1024:21307
    confirmCta: "Confirm My Appointment", // 1024:21389
    sending: "Sending...",
    // Review-step failure banners. ineligible = the legacy Aspen scheduler's
    // approved dialog body (aspen-scheduler messages/en-us.json
    // alreadyHasApptBody), phone substituted; unavailable = the frozen
    // widget's send-failure copy.
    ineligibleError: "Please call {phone} to schedule your appointment.",
    unavailableError:
      "We could not send your appointment request. Try again or call {phone} and the office will find a time with you.",
  },
  confirmation: {
    /** "You're scheduled, {Name}!" (1024:21401). */
    heading: "You’re scheduled, {firstName}!",
    dateTime: "Date & Time", // 1024:24265
    location: "Location", // 1024:24275
    addToCalendar: "Add to Calendar", // 1024:24271
    directions: "Directions", // 1024:24283
    call: "Call", // 1024:24287
    /**
     * ICS SUMMARY line; PRODID and the file name stay in code. Office and
     * date/time only, no visit type: the .ics lands in whatever calendar the
     * patient imports it into, which is outside the site's PHI controls
     * (legal-2 lens, 2026-09-08). {service} stays available should TAG legal
     * accept naming the visit type in the patient's calendar.
     */
    calendarSummary: "Aspen Dental appointment",
    paperwork: "Fill out your paperwork online before your appointment.", // 1024:21440
    fillOutForms: "Fill Out Forms", // 1024:21444
    insuranceHeading: "Most Dental Insurance Accepted{footnote}", // 1024:21457 (superscript 1 in the source run)
    insuranceFootnoteMarker: "1", // 1024:21457 / 1024:21645
    /**
     * "We accept most dental insurance providers." is a HIDDEN layer on every
     * board (1024:24311 emergency, 1024:21458 dentures; no node on the D
     * frames), so it ships empty (D-01 / G61). The board string stays in the
     * preset's variable note for an author who wants it back.
     */
    insuranceSubheading: "",
    seeAllInsurance: "See All Insurance Providers", // 1024:21543
    noInsuranceHeading: "No Insurance? \nNo Problem", // 1024:21548 (literal line break verbatim)
    noInsuranceBody:
      "Not having dental insurance doesn’t mean you have to skip care. Enroll in the Aspen Dental Savings Plan{footnote} to save 10-30%. Flexible payment options are available.", // 1024:21549 (superscript 2 in the source run)
    savingsPlanFootnoteMarker: "2", // 1024:21549 / 1024:21646
    enrollToday: "Enroll Today", // 1024:21551
    meetYourTeam: "Meet Your Team", // 1024:21564
    /** "Meet our dentists in Name/City, ST" (1024:21565). */
    meetSubheading: "Meet our dentists in {cityState}",
    haveQuestions: "Have Questions?", // 1024:21601
    disclaimerInsurance:
      "{footnote}Insurance acceptance varies by location — please call ahead to confirm your plan is accepted.", // 1024:21645 (em dash verbatim)
    disclaimerSavingsPlan:
      "{footnote}The Aspen Dental Savings Plan is NOT insurance. Please see plan terms and conditions for details.", // 1024:21646
    // ── Success page imagery and destinations (WP4, 2026-09-08) ─────────────
    /**
     * Hero photograph (1024:24251 M 375x250, 1024:24502 D 1440x720). The
     * boards' exact front-desk shot (imageRef eebad5d8) is not in Brandfolder;
     * this is the closest Aspen office photograph there: "PH AD Louisville KY
     * (Dixie Highway) front desk 1377", The Aspen Group DAM, 7726x5153 JPG,
     * chosen from the 2026-09-08 inventory (front desk / reception searches).
     * A per-service board photo replaces it through the preset once TAG uploads it.
     */
    heroImageSrc:
      "https://cdn.bfldr.com/LP3GHVAP/as/233tpzbgbpfk8hgb8f6hk6wg/PH_AD_Louisville_KY_Dixie_Highway_front_desk_1377",
    heroImageAlt: "The front desk and waiting area of an Aspen Dental office",
    /**
     * Patient-forms destination for the paperwork band. aspendental.com has no
     * patient-forms page BB can find (every candidate path 404s, 2026-09-08)
     * and the URL is TAG's to give, so it ships empty and the band stays
     * hidden until it is authored (a pill with nowhere to go is never shown).
     */
    fillOutFormsHref: "",
    /** Live aspendental.com pages, verified 2026-09-08 (200; linked from the home nav). */
    seeAllInsuranceHref: "/dental-insurance/",
    enrollTodayHref: "/pricing-offers/dental-savings-plan/",
    /**
     * FAQ header photograph (1024:24451 M 327x240, 1024:24706 D 600x471). The
     * boards' hygienist-at-the-exam-light shot (imageRef ad6bb691) is not in
     * Brandfolder; the closest Aspen clinical photograph there is "PH AD Siloam
     * Springs AR AK Action Shots 4341 041" (DAM, 4246x2831 JPG).
     */
    faqImageSrc:
      "https://cdn.bfldr.com/LP3GHVAP/as/48qk3rqffcwmxj3wbckfnhz/PH_AD_Siloam_Springs_AR_AK_Action_Shots_4341_041",
    faqImageAlt: "An Aspen Dental dentist and assistant caring for a patient in an exam room",
  },
  /**
   * Insurance carriers in the "Most Dental Insurance Accepted" logo band, in
   * board order (1024:24314..24379 M, 1024:24556..24619 D: Aetna-Logo_White,
   * UHC_Lockup, Delta_Dental_Logo, MetLife_logo, Humana-Logo,
   * blue-cross-blue-shield-vector-logo). logoSrc is the Brandfolder CDN URL
   * of the mark: all six are in The Aspen Group DAM as 148px-wide SVGs named
   * after the same Figma layers (inventory 2026-09-08, TAG-197), so the tiles
   * render the official marks with nothing authored. A tile with no URL is
   * dropped; a carrier name is never drawn as a text tile.
   */
  insuranceCarriers: [
    {
      name: "Aetna",
      logoSrc: "https://cdn.bfldr.com/LP3GHVAP/as/xf3fj5g7bbmsrvh4gpnwtkbx/Aetna-Logo_White_1", // svg 148x31
    },
    {
      name: "UnitedHealthcare",
      logoSrc: "https://cdn.bfldr.com/LP3GHVAP/as/x8bbwkmmngzfqcwkbk47krk/UHC_Lockup_wht_RGB_1", // svg 148x47
    },
    {
      name: "Delta Dental",
      logoSrc:
        "https://cdn.bfldr.com/LP3GHVAP/as/cb232fshc5f5m7brck9vc4k/Delta_Dental_Logo_Black_1", // svg 148x17
    },
    {
      name: "MetLife",
      logoSrc: "https://cdn.bfldr.com/LP3GHVAP/as/3m8h7gspmqt7g9xgtzpb63/MetLife_logo_Black_1", // svg 148x32
    },
    {
      name: "Humana",
      logoSrc: "https://cdn.bfldr.com/LP3GHVAP/as/b4hrkg7v9f3hfs32pmv3mgt/Humana-Logo_Black_1", // svg 148x31
    },
    {
      name: "Blue Cross Blue Shield",
      logoSrc:
        "https://cdn.bfldr.com/LP3GHVAP/as/8s454qqqxvhq36fv7b5tx/blue-cross-blue-shield-vector-logo_1", // svg 148x31
    },
  ],
} as const;
